import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../config";

// Generate Gravatar URL from email
function gravatarUrl(email, size = 200) {
  if (!email) return null;
  const hash = email.trim().toLowerCase();
  // Simple hash for demo - in production you'd use MD5
  // For now, we'll just use a default avatar service
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(email.split('@')[0])}&size=${size}&background=163a2f&color=e8efe8&bold=true`;
}

// normalize possibly-relative URLs to absolute using API_BASE
// Also fix hardcoded localhost:5002 references
function toAbsolute(url) {
  if (!url) return "";
  let s = String(url);
  
  // Fix hardcoded localhost:5002 references - replace with current API_BASE
  if (s.includes('localhost:5002') || s.includes('localhost:5001')) {
    // Extract just the path part (e.g., /uploads/avatars/3.jpg)
    const match = s.match(/\/uploads\/[^?#]+/);
    if (match) {
      s = match[0]; // Now it's a relative path
    }
  }
  
  // If already absolute, return as-is
  if (/^(https?:)?\/\//i.test(s)) return s;
  
  // Special handling for /uploads paths - these should go to server root, not API_BASE
  if (s.startsWith('/uploads/')) {
    // Get the protocol and host from API_BASE or window.location
    const base = API_BASE.replace(/\/api$/, ''); // Remove /api suffix if present
    if (base.startsWith('http')) {
      // API_BASE is absolute (e.g., http://localhost:5001/api)
      return base + s;
    } else {
      // API_BASE is relative (e.g., /api), use current origin
      if (typeof window !== 'undefined') {
        return window.location.origin + s;
      }
      return s;
    }
  }
  
  // For other paths, use API_BASE normally
  return `${API_BASE.replace(/\/$/, '')}/${s.replace(/^\//, '')}`;
}

function initialsFrom(name) {
  const s = String(name || '').trim();
  if (!s) return '?';
  const p = s.split(/\s+/);
  const a = (p[0]?.[0] || '').toUpperCase();
  const b = (p[1]?.[0] || '').toUpperCase();
  return (a + b) || a || '?';
}

function compactUserTag(name) {
  const cleaned = String(name || '')
    .replace(/[^\p{L}\s-]/gu, ' ')
    .trim();
  if (!cleaned) return '?';

  const parts = cleaned.split(/\s+/).filter(Boolean);
  const firstName = String(parts[0] || '');
  const lastName = String(parts.slice(1).join(' ') || '');

  const firstPart = firstName.slice(0, 4);
  const remaining = Math.max(0, 6 - firstPart.length);
  const lastPart = remaining > 0 ? lastName.slice(0, Math.min(4, remaining)) : '';

  const value = `${firstPart}${lastPart}`.trim();
  if (!value) return initialsFrom(name);
  return value.toUpperCase();
}

// Simple in-memory cache for fetched avatars
const avatarCache = new Map(); // key: userId -> { url: string|null, email: string|null }

export default function Avatar({ userId, name, src, size = 40, style = {}, className = "", title = "" }) {
  const [fetched, setFetched] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  // Compute a candidate src: explicit prop > fetched > gravatar > null
  const resolvedSrc = useMemo(() => {
    if (src) return toAbsolute(src);
    if (fetched) return toAbsolute(fetched);
    if (userEmail) return gravatarUrl(userEmail, size * 2);
    return null;
  }, [src, fetched, userEmail, size]);

  useEffect(() => {
    let mounted = true;
    const id = userId != null ? String(userId).match(/\d+/)?.[0] : null;
    if (!id || id === '0') return; // Skip invalid or zero IDs
    // if cached, use it immediately
    if (avatarCache.has(id)) {
      const cached = avatarCache.get(id);
      setFetched(cached.url);
      setUserEmail(cached.email);
      return;
    }
    // fetch user to get avatar_url and email
    (async () => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
        const r = await fetch(`${API_BASE}/users/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const j = await r.json().catch(() => ({}));
        const url = j && j.avatar_url ? String(j.avatar_url) : null;
        const email = j && j.email ? String(j.email) : null;
        const abs = url ? toAbsolute(url) : null;
        avatarCache.set(id, { url: abs, email });
        if (mounted) {
          setFetched(abs);
          setUserEmail(email);
        }
      } catch {
        avatarCache.set(id, { url: null, email: null });
        if (mounted) {
          setFetched(null);
          setUserEmail(null);
        }
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  const baseStyle = {
    width: size,
    height: size,
    borderRadius: size,
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#163a2f',
    color: '#e8efe8',
    fontSize: Math.max(10, Math.round(size * 0.36)),
    fontWeight: 800,
    border: '1px solid #2f6b57',
    ...style,
  };

  if (resolvedSrc) {
    return (
      <img
        alt={title || name || 'avatar'}
        src={resolvedSrc}
        className={className}
        style={{ ...baseStyle, objectFit: 'cover' }}
        title={title || name || ''}
      />
    );
  }

  return (
    <span className={className} style={baseStyle} title={title || name || ''}>
      {compactUserTag(name)}
    </span>
  );
}
